import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { User } from 'lucide-react';

// Datos de perfil del usuario interno. NO es un peaje: se puede posponer.
// Antes exigía dni/cuil/cbu/banco sin ninguna forma de cerrarlo — ni botón, ni Escape, ni
// click afuera — así que a quien no tenía esos datos lo dejaba literalmente afuera del
// helpdesk. Le pasó a Thiago (pasante, no cobra sueldo por acá) el 27/08: no pudo trabajar.
// Ahora sólo son obligatorios los datos de CONTACTO; los de liquidación quedan opcionales
// y visibles para quien sí los tenga que cargar.
export default function ProfileCompletionModal({ user, onComplete, onSkip }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    birth_date: '',
    phone: '',
    dni: '',
    cuil: '',
    cbu: '',
    alias_cbu: '',
    bank_name: '',
  });
  const [saving, setSaving] = useState(false);

  // Pre-fill from existing data.
  // Deliberately NOT /hr/profile/:id: that route sits behind requireVaultToken, so
  // for anyone who has never set a vault PIN it 401s (code VAULT_REQUIRED). The GET
  // failing was invisible (the .catch below swallows it) — the form just came up
  // blank — but the PATCH failing turned this nag into a dead end: it asks you for
  // data and then errors on every single save. That's what blocked Thiago on
  // 31-ago-2026, and it blocks any agent without the HR module the same way.
  // /agent-onboarding is the self-service path: same employee_profiles columns,
  // same camelCase payload keys, scoped to the caller, no PIN.
  useEffect(() => {
    api.get('/agent-onboarding/status').then(({ data }) => {
      const prof = data?.profile;
      if (prof) {
        setForm(f => ({
          ...f,
          birth_date: prof.birth_date ? String(prof.birth_date).split('T')[0] : '',
          phone: prof.phone || '',
          dni: prof.dni || '',
          cuil: prof.cuil || '',
          cbu: prof.cbu || '',
          alias_cbu: prof.alias_cbu || '',
          bank_name: prof.bank_name || '',
        }));
      }
    }).catch(() => {});
  }, [user.id]);

  const canSubmit = !!(form.birth_date && form.phone);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    try {
      const payload = {};
      const put = (key, val) => { if (val && String(val).trim()) payload[key] = String(val).trim(); };
      put('birthDate', form.birth_date); put('phone', form.phone);
      put('dni', form.dni); put('cuil', form.cuil);
      put('cbu', form.cbu); put('aliasCbu', form.alias_cbu); put('bankName', form.bank_name);
      await api.patch('/agent-onboarding/me/profile', payload);
      toast.success(t('agents.profileSaved'));
      onComplete();
    } catch {
      toast.error(t('common.failed'));
    } finally {
      setSaving(false);
    }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const modal = (
    <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onSkip?.(); }}>
      <div className="modal" style={{ maxWidth: 480, padding: 0 }}>
        <div style={{ padding: '24px 24px 0', textAlign: 'center' }}>
          <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'var(--c-primary)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
            <User size={24} color="#fff" />
          </div>
          <h3 style={{ margin: '0 0 4px', fontSize: 18 }}>{t('profile.completeTitle') || 'Completá tu perfil'}</h3>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--c-text-muted)' }}>
            {t('profile.completeDesc') || 'Necesitamos algunos datos personales y bancarios para continuar.'}
          </p>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="grid-2" style={{ gap: 12 }}>
              <div>
                <label className="form-label">{t('agents.birthDate')} *</label>
                <input className="form-input" type="date" value={form.birth_date} onChange={e => set('birth_date', e.target.value)} required />
              </div>
              <div>
                <label className="form-label">{t('common.phone')} *</label>
                <input className="form-input" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+54 9 11..." required />
              </div>
            </div>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--c-text-muted)' }}>
              {t('profile.payrollFieldsHint')}
            </p>
            <div className="grid-2" style={{ gap: 12 }}>
              <div>
                <label className="form-label">{t('agents.dni')}</label>
                <input className="form-input" value={form.dni} onChange={e => set('dni', e.target.value)} placeholder="12345678" />
              </div>
              <div>
                <label className="form-label">{t('agents.cuil')}</label>
                <input className="form-input" value={form.cuil} onChange={e => set('cuil', e.target.value)} placeholder="20-12345678-9" />
              </div>
            </div>
            <div>
              <label className="form-label">{t('agents.bank')}</label>
              <input className="form-input" value={form.bank_name} onChange={e => set('bank_name', e.target.value)} placeholder="Banco Galicia" />
            </div>
            <div>
              <label className="form-label">{t('agents.cbu')}</label>
              <input className="form-input" value={form.cbu} onChange={e => set('cbu', e.target.value)} placeholder="22 dígitos" style={{ fontFamily: 'var(--font-mono)' }} />
            </div>
            <div>
              <label className="form-label">{t('agents.aliasCbu')}</label>
              <input className="form-input" value={form.alias_cbu} onChange={e => set('alias_cbu', e.target.value)} placeholder="mi.alias.cbu" style={{ fontFamily: 'var(--font-mono)' }} />
            </div>
          </div>
          <div style={{ padding: '12px 24px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            <button type="button" className="btn btn-ghost" onClick={() => onSkip?.()} disabled={saving}>
              {t('profile.maybeLater')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving || !canSubmit}>
              {saving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
}
